import { useCallback, useEffect, useRef, useState } from 'react';

import { ShortUrl } from '@air/api';
import { type Task } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';
import { FIVE_MINUTES, THIRTY_SECONDS } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { useEventSubscription } from '~/hooks/useEventSubscription';

/** Max expected task runtime plus slack for queue/dispatch before the worker clock starts. */
const FAILSAFE_FETCH_DELAY_MS = FIVE_MINUTES + THIRTY_SECONDS;

export type DispatchFormSessionSubmitterTaskParams = {
  formId: string;
  sessionId: string;
  submitterName?: string;
};

export type UseFormSessionSubmitterTaskParams = {
  onSucceeded: () => void;
  onFailed: (failureCode: string | undefined) => void;
};

/**
 * Dispatches and tracks an async content collection form submission as a `FormSessionSubmitter`
 * task. Primary completion path is `System.Task.completed` over the public short-URL socket. HTTP
 * fallbacks: one `Tasks.getTask` when the subscription becomes active (fast completion before
 * subscribe, or after reconnect), and one failsafe fetch after {@link FAILSAFE_FETCH_DELAY_MS}.
 * `cancelled` is treated like failure (stop spinner, generic error UX). If the failsafe runs
 * without a prior WSS terminal event, reports once to Bugsnag (distinct errors for still-open vs
 * terminal-via-fetch).
 */
export const useFormSessionSubmitterTask = ({ onSucceeded, onFailed }: UseFormSessionSubmitterTaskParams) => {
  const { workspaceId } = usePublicWorkspace();
  const { shortId } = useShortIdContext();
  const [taskId, setTaskId] = useState<string | null>(null);

  // Guards against handling a terminal state more than once (e.g. WS event + reconnect refresh).
  const handledRef = useRef(false);
  const taskIdRef = useRef(taskId);

  useEffect(() => {
    taskIdRef.current = taskId;
  }, [taskId]);

  // Reset idempotency only when tracking a new task — not when clearing to `null` after terminal handling,
  // so a stale in-flight `getTask` cannot fire callbacks again.
  useEffect(() => {
    if (taskId != null) {
      handledRef.current = false;
    }
  }, [taskId]);

  const tryResolveTerminalTask = useCallback(
    (task: Pick<Task, 'id' | 'status' | 'data'>) => {
      if (task.id !== taskIdRef.current) {
        return;
      }

      if (handledRef.current) {
        return;
      }

      if (task.status === 'succeeded') {
        handledRef.current = true;
        setTaskId(null);
        onSucceeded();
      } else if (task.status === 'failed' || task.status === 'exception' || task.status === 'cancelled') {
        handledRef.current = true;
        setTaskId(null);
        const log = task.data?.log ?? [];
        onFailed(log[log.length - 1]);
      }
    },
    [onSucceeded, onFailed],
  );

  const getTrackedTask = useCallback(async () => {
    if (!taskId || handledRef.current) {
      return null;
    }

    return ShortUrl.Tasks.getTask(shortId, { taskId });
  }, [taskId, shortId]);

  const fetchAndHandleTask = useCallback(async () => {
    try {
      const task = await getTrackedTask();

      if (task) {
        tryResolveTerminalTask(task);
      }
    } catch (error) {
      reportErrorToBugsnag({
        error,
        context: 'Failed to refresh form session submitter task',
      });
    }
  }, [getTrackedTask, tryResolveTerminalTask]);

  const runFailsafe = useCallback(async () => {
    if (!taskId || handledRef.current) {
      return;
    }

    try {
      const task = await getTrackedTask();

      if (!task || task.id !== taskIdRef.current) {
        return;
      }

      tryResolveTerminalTask(task);

      reportErrorToBugsnag({
        error: new Error(
          handledRef.current
            ? 'System.Task.completed not handled in time; terminal state resolved via failsafe fetch'
            : 'Task still open after failsafe window',
        ),
        context: 'FormSessionSubmitter failsafe fetch ran without a prior WSS terminal event',
        metadata: { task: { taskId: task.id, status: task.status } },
      });
    } catch (error) {
      reportErrorToBugsnag({
        error,
        context: 'Failed to run FormSessionSubmitter failsafe fetch',
      });
    }
  }, [taskId, getTrackedTask, tryResolveTerminalTask]);

  const { isSubscribed } = useEventSubscription<'System.Task.completed'>({
    currentWorkspaceId: workspaceId,
    event: taskId ? { eventType: 'System.Task.completed', pattern: { id: taskId } } : undefined,
    options: {
      onUpdate: ({ data }) => {
        if (data.id !== taskId) {
          return;
        }

        tryResolveTerminalTask(data);
      },
    },
  });

  // One HTTP catch-up when the task.completed subscription is active (fast task that finished
  // before subscribe, or reconnect after convertSubscriptionsToPending reset subs)
  useEffect(() => {
    if (isSubscribed) {
      void fetchAndHandleTask();
    }
  }, [isSubscribed, fetchAndHandleTask]);

  // Arm a single failsafe after FAILSAFE_FETCH_DELAY_MS. WSS should deliver System.Task.completed
  // sooner; if it does not, runFailsafe re-fetches and either unblocks the UI or reports to Bugsnag.
  // Cleared when taskId clears or changes so we do not fire after success or on a different task.
  useEffect(() => {
    if (!taskId) {
      return;
    }

    const timeoutId = setTimeout(() => void runFailsafe(), FAILSAFE_FETCH_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [taskId, runFailsafe]);

  const dispatchFormSessionSubmitterTask = useCallback(
    async ({ formId, sessionId, submitterName }: DispatchFormSessionSubmitterTaskParams) => {
      const task = await ShortUrl.Tasks.createTask(shortId, {
        type: 'FormSessionSubmitter',
        args: { formId, sessionId, submitterName },
      });

      setTaskId(task.id);
    },
    [shortId],
  );

  return { dispatchFormSessionSubmitterTask };
};
